import {
  SERVICE_ALIASES,
  SERVICE_SHORTENED_NAMES,
  SERVICE_CATEGORY_EMPTY_STATE
} from './constants'
import { rest } from 'services'

export const formatPhoneNumber = phoneNumber =>
  `(${phoneNumber.areaCode}) ${phoneNumber.firstThree}-${phoneNumber.lastFour}`

export const getServiceIconName = categoryId =>
  categoryId.replace(/^cat-/, '')

export const getServiceShortenedName = (categoryId, categoryName) =>
  SERVICE_SHORTENED_NAMES[categoryId] || categoryName

export const getServiceAlias = (categoryId, categoryName) =>
  SERVICE_ALIASES[categoryId] || categoryName

export const getServiceCategoryURL = (categoryId, serviceCategories) => {
  const { storeServiceCategorySeo = '' } = serviceCategories.find(srv => srv.categoryId === categoryId) || {}
  return storeServiceCategorySeo
}

export const generateListOfReviewsForSchema = (reviewsList) => {
  const generatedListOfReviewsForSchema = reviewsList.map((review) => {
    const { ReviewerName, ReviewMessage, SurveyDate } = review

    return {
      '@type': 'Review',
      name: ReviewerName,
      reviewBody: ReviewMessage,
      datePublished: SurveyDate,
      author: {
        '@type': 'Person',
        name: ReviewerName
      }
    }
  })

  return generatedListOfReviewsForSchema
}

export const generateReviewsPartOfSchema = ({
  generatedReviewsList,
  numberOfReviews,
  averageRating
}) => ({
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: Number(averageRating.toFixed(1)),
    bestRating: 5,
    worstRating: 1,
    ratingCount: numberOfReviews
  },
  review: generatedReviewsList
})

export const generateServiceSchema = ({ storeCity, categoryId, categoryName, metaDescription }) => {
  const serviceName = getServiceAlias(categoryId, categoryName)

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `${storeCity} ${serviceName}`,
    description: metaDescription,
    serviceType: serviceName
  }
}

export const generateServiceCategorySchema = ({
  parentStoreUrl,
  canonicalUrl,
  storeCity,
  storeNumber,
  metaDescription,
  storeEmail,
  storePhoneNumber,
  storeAddress,
  storeState,
  storeZip,
  storeStateFull,
  storeLatitude,
  storeLongitude,
  reviewsPartForSchema
}) => {
  const serviceCategorySchema = {
    '@context': 'https://schema.org/',
    '@type': [
      'LocalBusiness',
      'webPage',
      'TireShop',
      'AutoRepair',
      'AutomotiveBusiness'
    ],
    mainEntityOfPage: {
      '@type': [
        'WebPage',
        'TireShop',
        'AutoRepair',
        'AutomotiveBusiness'
      ],
      '@id': parentStoreUrl
    },
    url: canonicalUrl,
    '@id': canonicalUrl,
    name: `Big O Tires ${storeCity}`,
    provider: 'Big O Tires',
    branchCode: storeNumber,
    description: metaDescription,
    alternateName: 'Tire Shop Near Me',
    email: storeEmail,
    telephone: storePhoneNumber,
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: storePhoneNumber,
        contactType: 'Store Phone Number',
        availableLanguage: 'English'
      }
    ],
    image: 'https://www.bigotires.com/static/media/Logo.1b4cdbf8.svg',
    speakable: {
      '@type': 'SpeakableSpecification',
      xPath: [
        '/html/head/title',
        '/html/head/meta[@name="description"]/@content'
      ]
    },
    significantLink: 'https://www.bigotires.com/appointment/quick-appointment',
    potentialAction: {
      '@type': 'ReserveAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: '/appointment/quick-appointment',
        inLanguage: 'en-US',
        actionPlatform: [
          'http://schema.org/DesktopWebPlatform',
          'http://schema.org/IOSPlatform',
          'http://schema.org/AndroidPlatform'
        ]
      },
      result: {
        '@type': 'Reservation',
        name: 'Book an appointment'
      },
      name: 'Big O Tires',
      hasPOS: [],
      url: canonicalUrl,
      image: 'https://www.bigotires.com/static/media/Logo.1b4cdbf8.svg'
    },
    additionalProperty: [
      {
        '@type': 'PropertyValue',
        propertyID: 'appointments',
        value: true
      },
      {
        '@type': 'PropertyValue',
        propertyID: 'street',
        value: storeAddress
      }
    ],
    hasMap: {
      '@type': 'Map',
      mapType: 'ParkingMap',
      url: `https://maps.google.com\\/maps?q=${storeAddress},${storeCity},${storeState}`
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: storeAddress,
      addressLocality: storeCity,
      addressRegion: storeState,
      postalCode: storeZip,
      addressCountry: 'US'
    },
    containedInPlace: {
      '@type': 'Place',
      name: storeZip,
      alternateName: `${storeZip}, ${storeStateFull}, United States`,
      containedInPlace: {
        '@type': 'City',
        name: storeCity,
        alternateName: `${storeCity}, ${storeStateFull}, United States`,
        containedInPlace: {
          '@type': 'State',
          name: storeStateFull,
          alternateName: `${storeStateFull}, United States`,
          containedInPlace: {
            '@type': 'Country',
            name: 'United States',
            alternateName: 'United States',
            address: {
              '@type': 'PostalAddress',
              addressCountry: 'US'
            }
          },
          address: {
            '@type': 'PostalAddress',
            addressCountry: 'US',
            addressRegion: storeState
          }
        },
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'US',
          addressLocality: storeCity,
          addressRegion: storeState
        }
      },
      url: canonicalUrl,
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'US',
        addressLocality: storeCity,
        addressRegion: storeState,
        postalCode: storeZip
      }
    },
    audience: {
      '@type': 'Audience',
      name: [
        'Car Owners',
        'Fleet Managers',
        'Business Owners'
      ]
    },
    areaServed: {
      '@type': 'GeoCircle',
      geoMidpoint: {
        '@type': 'GeoCoordinates',
        latitude: storeLatitude,
        longitude: storeLongitude
      },
      geoRadius: '1000'
    },
    currenciesAccepted: 'USD',
    paymentAccepted: [
      'USD',
      'Visa',
      'MasterCard',
      'American Express',
      'Discover'
    ],
    priceRange: 'Varies by service provided',
    sameAs: [
      'https://www.facebook.com/BigOTires',
      'https://www.twitter.com/bigotires',
      'https://www.instagram.com/bigotiresofficial/'
    ],
    openingHoursSpecification: [
      {
        '@context': 'http://schema.org',
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'mon',
        opens: '07:00',
        closes: '18:00'
      },
      {
        '@context': 'http://schema.org',
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'tue',
        opens: '07:00',
        closes: '18:00'
      },
      {
        '@context': 'http://schema.org',
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'wed',
        opens: '07:00',
        closes: '18:00'
      },
      {
        '@context': 'http://schema.org',
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'thu',
        opens: '07:00',
        closes: '18:00'
      },
      {
        '@context': 'http://schema.org',
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'fri',
        opens: '07:00',
        closes: '18:00'
      },
      {
        '@context': 'http://schema.org',
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'sat',
        opens: '07:00',
        closes: '18:00'
      },
      {
        '@context': 'http://schema.org',
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'sun',
        opens: '',
        closes: ''
      }
    ],
    mentions: [
      {
        '@type': 'WebPage',
        name: 'Schedule Tire Installation',
        url: 'https://www.bigotires.com/appointment/quick-appointment',
        '@id': 'https://www.bigotires.com/appointment/quick-appointment'
      },
      {
        '@type': 'WebPage',
        name: 'Shop for New Tires',
        url: 'https://www.bigotires.com/tires/',
        '@id': 'https://www.bigotires.com/tires/'
      },
      {
        '@type': 'WebPage',
        name: 'Shop for New Tire by Vehicles',
        url: 'https://www.midas.com/tiresearch',
        '@id': 'https://www.midas.com/tiresearch'
      },
      {
        '@type': 'WebPage',
        name: 'Auto Repair Services',
        url: 'https://www.bigotires.com/services/',
        '@id': 'https://www.bigotires.com/services/'
      },
      {
        '@type': 'WebPage',
        name: 'Local Deals and Specials',
        url: 'https://www.bigotires.com/deals',
        '@id': 'https://www.bigotires.com/deals'
      },
      {
        '@type': 'WebPage',
        name: `${storeCity} Tire Services and Repair Near Me`,
        url: `${parentStoreUrl}/tire-services-and-repair`,
        '@id': `${parentStoreUrl}/tire-services-and-repair`
      },
      {
        '@type': 'WebPage',
        name: `${storeCity} Wheel Alignment Near Me`,
        url: `${parentStoreUrl}/wheel-alignment`,
        '@id': `${parentStoreUrl}/wheel-alignment`
      },
      {
        '@type': 'WebPage',
        name: `${storeCity} Car Batteries Near Me`,
        url: `${parentStoreUrl}/car-batteries`,
        '@id': `${parentStoreUrl}/car-batteries`
      },
      {
        '@type': 'WebPage',
        name: `${storeCity} Brake Repair Near Me`,
        url: `${parentStoreUrl}/brake-repair`,
        '@id': `${parentStoreUrl}/brake-repair`
      },
      {
        '@type': 'WebPage',
        name: `${storeCity} Oil Change, Lube and Filter Near Me`,
        url: `${parentStoreUrl}/oil-change-lube-and-filters`,
        '@id': `${parentStoreUrl}/oil-change-lube-and-filters`
      },
      {
        '@type': 'WebPage',
        name: `${storeCity} Routine Auto Service and Fluids Near Me`,
        url: `${parentStoreUrl}/routine-auto-services-and-fluids`,
        '@id': `${parentStoreUrl}/routine-auto-services-and-fluids`
      },
      {
        '@type': 'WebPage',
        name: `${storeCity} Suspension and Front End Services Near Me`,
        url: `${parentStoreUrl}/suspension-and-front-end-services`,
        '@id': `${parentStoreUrl}/suspension-and-front-end-services`
      }
    ]
  }

  const fullServiceCategorySchema = {
    ...serviceCategorySchema,
    ...reviewsPartForSchema
  }

  return fullServiceCategorySchema
}

export const generateBreadcrumbsSchema = ({
  storeState,
  parentStoreUrl,
  storeCity
}) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://www.bigotires.com/'
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Find a Store',
      item: 'https://www.bigotires.com/location'
    },
    {
      '@type': 'ListItem',
      position: 3,
      name: storeState,
      item: `https://www.bigotires.com/location/${storeState}`
    },
    {
      '@type': 'ListItem',
      position: 4,
      name: storeCity,
      item: `https://www.bigotires.com/location/${storeCity}`
    },
    {
      '@type': 'ListItem',
      position: 4,
      name: `Big O Tires ${storeCity}`,
      item: parentStoreUrl
    }
  ]
})

export const getCategoryIdFromPath = (path) => {
  if (!path) return ''

  const splittedPath = path.split('/')
  const pathLength = splittedPath.length

  return splittedPath[pathLength - 1]
}

export const updateServicesSection = async (categoryId) => {
  if (categoryId) {
    try {
      const {
        categoryName,
        categoryLongDescription,
        serviceCategorySeo: serviceUrl
      } = await rest.api.getServiceCategoryDetails({ categoryId })

      return {
        categoryId,
        categoryName,
        categoryLongDescription,
        serviceUrl
      }
    } catch (error) {
      console.error('Error fetching service category details:', error)
      return SERVICE_CATEGORY_EMPTY_STATE
    }
  } else {
    return SERVICE_CATEGORY_EMPTY_STATE
  }
}

export const removeLastSegment = (url) => {
  if (typeof url !== 'string') {
    throw new TypeError('The input must be a string')
  }

  const lastSlashIndex = url.lastIndexOf('/')
  if (lastSlashIndex === -1) {
    return url
  }

  return url.substring(0, lastSlashIndex)
}
